import * as THREE from 'three';
import { DirtyRange, FOG_FRAG, FOG_VERT, fxUniforms, quadGeometry } from './common';

/**
 * Persistent toxic puddles: one alpha-blended, ground-aligned draw for all of
 * them, and none when the courtyard is clean. The shader spreads the liquid in,
 * animates its sheen and bursting bubbles, then dries it from the rim.
 */
export class VenomPools {
  readonly mesh: THREE.Mesh;
  private readonly shape: THREE.InstancedBufferAttribute;
  private readonly life: THREE.InstancedBufferAttribute;
  private readonly dirty = new DirtyRange();
  private readonly ends: Float32Array;
  private count = 0;

  constructor(readonly capacity = 32) {
    const g = quadGeometry();
    this.shape = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.life = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('iShape', this.shape); g.setAttribute('iLife', this.life);
    g.instanceCount = 0;
    this.ends = new Float32Array(capacity);
    this.mesh = new THREE.Mesh(g, new THREE.ShaderMaterial({
      uniforms: fxUniforms,
      transparent: true, depthWrite: false,
      vertexShader: /* glsl */ `
        attribute vec4 iShape; attribute vec4 iLife;
        uniform float uTime;
        varying vec2 vUv; varying float vAge, vLeft, vSeed;
        ${FOG_VERT}
        void main() {
          vAge = uTime - iLife.x; vLeft = iLife.y - uTime; vSeed = iShape.w;
          if (vLeft <= 0.0 || iShape.z <= 0.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
          vUv = (uv * 2.0 - 1.0) * 1.15;
          vec4 mv = modelViewMatrix * vec4(iShape.x + position.x * iShape.z * 2.3, 0.04 + vSeed * 0.004, iShape.y - position.y * iShape.z * 2.3, 1.0);
          FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        varying vec2 vUv; varying float vAge, vLeft, vSeed;
        ${FOG_FRAG}
        float h21(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5); }
        void main() {
          float ang = atan(vUv.y, vUv.x), s = vSeed * 40.0;
          // spread in quickly, then dry back from the rim over the last second
          float spread = smoothstep(0.0, 0.22, vAge) * (0.55 + 0.45 * smoothstep(0.0, 1.0, vLeft));
          float edge = 0.86 * spread + sin(ang * 5.0 + s) * 0.07 + sin(ang * 9.0 - s + vAge * 0.8) * 0.04 + sin(ang * 3.0 + s * 0.5) * 0.06;
          float d = length(vUv);
          // a dark wet stain soaks into the stone just past the liquid, seating the puddle in the floor
          if (d > edge * 1.16) discard;
          if (d > edge) {
            float wet = 1.0 - smoothstep(edge, edge * 1.16, d);
            gl_FragColor = vec4(vec3(0.0), wet * 0.4 * smoothstep(0.0, 0.5, vLeft));
            return;
          }
          float depth = 1.0 - d / edge;
          // moving sheen: two drifting interference layers
          // a slow-moving liquid surface: height from drifting waves, normal from its slope, one specular lobe
          vec2 q = vUv * 9.0 + s;
          float hx = cos(q.x + sin(q.y * 1.3 + uTime * 0.9)) * 0.5 + cos(q.x * 2.1 - uTime * 0.6) * 0.25;
          float hy = cos(q.y * 1.2 - uTime * 0.8 + s) * 0.5 + cos(q.y * 2.3 + q.x + uTime * 0.5) * 0.25;
          vec3 nrm = normalize(vec3(-hx * 0.35, -hy * 0.35, 1.0));
          float sheen = pow(max(0.0, dot(nrm, normalize(vec3(-0.35, 0.5, 1.8)))), 260.0) * 1.1;  // half-vector of key light and a top-down view
          // bubbles: cells that swell and pop on their own clock
          vec2 cell = floor(vUv * 5.0), f = fract(vUv * 5.0) - 0.5;
          float r = h21(cell + s), phase = fract(uTime * (0.5 + r * 0.7) + r * 7.0);
          float bubble = r > 0.78 ? smoothstep(0.05, 0.0, abs(length(f) - phase * 0.3)) * (1.0 - phase) * (1.0 - phase) : 0.0;
          float k = d / edge, rim = smoothstep(0.9, 0.975, k) * smoothstep(1.0, 0.98, k);
          // murky, deep toxin: olive-black at the meniscus, a sickly glow only from the depths
          vec3 liquid = mix(vec3(0.02, 0.045, 0.012), vec3(0.07, 0.16, 0.025), depth * depth);
          vec3 glow = vec3(0.35, 0.85, 0.16) * (rim * 0.14 + bubble * 0.8) + vec3(0.7, 0.95, 0.55) * sheen * depth;
          float alpha = (0.7 + 0.22 * depth) * smoothstep(0.0, 0.5, vLeft);
          // from the gameplay camera the glow is calmer; close up it keeps its sheen
          glow *= mix(1.0, 0.6, smoothstep(10.0, 18.0, vFogDepth));
          gl_FragColor = vec4((liquid + glow) * fogKeep(), alpha);
        }`,
    }));
    this.mesh.name = 'venomPools'; this.mesh.renderOrder = 4;
    this.mesh.visible = false; this.mesh.frustumCulled = false;
  }

  /** Lay a puddle (reuses the slot that dries first when full). */
  add(x: number, z: number, radius: number, duration: number) {
    const now = fxUniforms.uTime.value;
    // a splash landing in a live puddle feeds it instead of stacking another decal on top
    for (let i = 0; i < this.count; i++) {
      if (this.ends[i] <= now + 0.6) continue;
      const r0 = this.shape.getZ(i) * 0.86, dx = this.shape.getX(i) - x, dz = this.shape.getY(i) - z;
      if (Math.hypot(dx, dz) > (r0 + radius) * 0.6) continue;
      const grown = Math.min(1.1, Math.hypot(r0, radius * 0.6));
      this.shape.setZ(i, grown / 0.86);
      this.ends[i] = Math.max(this.ends[i], now + duration);
      this.life.setY(i, this.ends[i]);
      this.dirty.mark(i);
      return;
    }
    let slot = -1, soonest = Infinity;
    for (let i = 0; i < this.capacity; i++) {
      if (this.ends[i] <= now) { slot = i; break; }
      if (this.ends[i] < soonest) { soonest = this.ends[i]; slot = i; }
    }
    this.ends[slot] = now + duration;
    this.shape.setXYZW(slot, x, z, radius / 0.86, Math.random());
    this.life.setXYZW(slot, now, now + duration, 0, 0);
    this.dirty.mark(slot);
    this.count = Math.max(this.count, slot + 1);
  }

  /** Trim the instance range to live puddles and upload changed slots. */
  flush() {
    const now = fxUniforms.uTime.value;
    while (this.count > 0 && this.ends[this.count - 1] <= now) this.count--;
    (this.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = this.count;
    this.mesh.visible = this.count > 0;
    this.dirty.flush([this.shape, this.life]);
  }
}
